// First-run onboarding. Three short screens that commit the user to:
//   1. an exam date (sets prefs.examDate on the server via /api/coach)
//   2. a starting chapter (a hint, not a hard route)
//   3. a Today's-review tutorial nudge
//
// Shows once; persistence flag lives in localStorage under
// `aba-onboarding-done`. Skippable at any step.

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, BookOpen, Calendar, Sparkles, X } from "lucide-react";
import { saveExamDate } from "@/lib/coach";

const KEY = "aba-onboarding-done";

export function Onboarding() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [examDate, setExamDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(KEY)) return;
    const t = window.setTimeout(() => setOpen(true), 400);
    return () => window.clearTimeout(t);
  }, []);

  const close = () => {
    try {
      window.localStorage.setItem(KEY, new Date().toISOString());
    } catch {
      // ignore quota
    }
    setOpen(false);
  };

  const next = () => setStep(s => s + 1);

  const todayIso = new Date().toISOString().slice(0, 10);

  const onSaveDate = async () => {
    if (!examDate) {
      next();
      return;
    }
    if (examDate < todayIso) {
      setError("Pick a date today or later - the coach can't plan to a past exam.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveExamDate(examDate);
    } catch {
      // Server may not be wired (preview URL); don't block the flow.
      setError("Couldn't save to server - the coach will still use your local plan.");
    } finally {
      setSaving(false);
      next();
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6"
      data-testid="onboarding"
    >
      <div className="absolute inset-0 bg-background/85" onClick={close} aria-hidden="true" />
      <div
        className="relative w-full sm:max-w-md bg-card sm:rounded-xl border-t sm:border border-border shadow-2xl p-5 sm:p-6"
        onMouseDown={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 p-2 rounded-md hover-elevate text-muted-foreground"
          aria-label="Skip onboarding"
          data-testid="onboarding-skip"
        >
          <X className="w-4 h-4" />
        </button>

        {step === 0 && (
          <Step
            icon={Sparkles}
            eyebrow="Welcome"
            title="Pass the BCBA, faster"
            body="This app blends curated chapter content, spaced-repetition flashcards, mock exams and an AI coach to compress your prep. Two minutes of setup will personalise everything."
            primary="Set up"
            onPrimary={next}
          />
        )}

        {step === 1 && (
          <Step
            icon={Calendar}
            eyebrow="Step 1 of 3"
            title="When do you sit the exam?"
            body="If you have a target date, the AI coach biases your weekly plan toward mock-exam practice as the date approaches. Skippable - you can set or change it later from /coach."
            primary={examDate ? "Save and continue" : "Skip for now"}
            onPrimary={onSaveDate}
            disablePrimary={saving}
          >
            <input
              type="date"
              value={examDate}
              min={todayIso}
              onChange={e => setExamDate(e.target.value)}
              className="w-full mt-3 px-3 py-2.5 rounded-md border border-input bg-background text-body min-h-[44px]"
              data-testid="onboarding-exam-date"
            />
            {error && <p className="text-meta text-amber-600 dark:text-amber-400 mt-2">{error}</p>}
          </Step>
        )}

        {step === 2 && (
          <Step
            icon={BookOpen}
            eyebrow="Step 2 of 3"
            title="Pick a starting chapter"
            body="Chapter 1 grounds every other chapter and only takes ~25 minutes. You can switch any time from the library."
            primary="Open chapter 1"
            primaryHref="/chapter/01"
            onPrimary={close}
            secondary="Browse library"
            onSecondary={next}
          />
        )}

        {step === 3 && (
          <Step
            icon={Sparkles}
            eyebrow="Step 3 of 3"
            title="Make it a daily habit"
            body="As you rate flashcards (Again / Hard / Got it), they get scheduled across days. Open Today's review each morning to drill what's due - that's the single biggest exam-pass lever."
            primary="See Today's review"
            primaryHref="/today"
            onPrimary={close}
            secondary="Start later"
            onSecondary={close}
          />
        )}
      </div>
    </div>
  );
}

function Step({
  icon: Icon,
  eyebrow,
  title,
  body,
  primary,
  primaryHref,
  onPrimary,
  secondary,
  onSecondary,
  disablePrimary,
  children,
}: {
  icon: any;
  eyebrow: string;
  title: string;
  body: string;
  primary: string;
  primaryHref?: string;
  onPrimary?: () => void;
  secondary?: string;
  onSecondary?: () => void;
  disablePrimary?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-eyebrow text-muted-foreground mb-2 flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-primary" />
        {eyebrow}
      </div>
      <h2 className="text-lead mb-2">{title}</h2>
      <p className="text-body text-foreground/85 leading-relaxed">{body}</p>
      {children}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {primaryHref ? (
          <Link
            href={primaryHref}
            onClick={() => {
              window.localStorage.setItem(KEY, new Date().toISOString());
              onPrimary?.();
            }}
            className="inline-flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-md bg-primary text-primary-foreground text-body font-medium"
          >
            {primary} <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            disabled={disablePrimary}
            onClick={onPrimary}
            className="inline-flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-md bg-primary text-primary-foreground text-body font-medium disabled:opacity-50"
          >
            {primary} <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {secondary && (
          <button
            type="button"
            onClick={onSecondary}
            className="px-4 py-3 min-h-[44px] rounded-md border border-border text-body hover-elevate"
          >
            {secondary}
          </button>
        )}
      </div>
    </div>
  );
}
